import { Box, Divider, TextField, Typography } from "@mui/material";
import { FormikProps } from "formik";
import { makeSxStyles } from "ui";
import FieldRow from "@/shared/components/FieldRow";
import ReplyEmailField from "@/shared/components/ReplyEmailField";
import AddressDerivedFields from "./AddressDerivedFields";
import OrganizationAddressAutocomplete from "./OrganizationAddressAutocomplete";
import PromotionalBannerFields from "./PromotionalBannerFields";
import { AddOrganizationFormValues } from "../types/organization-form";

interface AddOrganizationFormProps {
  organizationForm: FormikProps<AddOrganizationFormValues>;
  homeImageError: string | null;
  onHomeImageErrorDismiss: () => void;
  placePredictions: google.maps.places.AutocompletePrediction[];
  getPlacePredictions: (params: { input: string }) => void;
  getAddressComponents: (
    placeId: string,
  ) => Promise<google.maps.GeocoderAddressComponent[] | null>;
}

const AddOrganizationForm = ({
  organizationForm,
  homeImageError,
  onHomeImageErrorDismiss,
  placePredictions,
  getPlacePredictions,
  getAddressComponents,
}: AddOrganizationFormProps) => {
  const styles = useStyles();
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setValues,
    setFieldValue,
    setFieldTouched,
  } = organizationForm;

  return (
    <Box sx={styles.container}>
      <Typography sx={styles.sectionTitle}>Organization Info</Typography>

      <FieldRow label="Organization Name" required>
        <TextField
          id="name"
          placeholder="Enter Name"
          value={values.name}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.name && !!errors.name}
          helperText={touched.name && errors.name}
          fullWidth
        />
      </FieldRow>
      <FieldRow label="Address" required>
        <OrganizationAddressAutocomplete
          value={values.address ?? ""}
          onAddressChange={(value, parsed) =>
            setValues((prev) => ({
              ...prev,
              address: value,
              city: parsed?.city ?? "",
              state: parsed?.state ?? "",
              zipCode: parsed?.zipCode ?? "",
            }))
          }
          onBlur={handleBlur}
          error={touched.address && !!errors.address}
          helperText={touched.address && errors.address}
          placePredictions={placePredictions}
          getPlacePredictions={getPlacePredictions}
          getAddressComponents={getAddressComponents}
        />
      </FieldRow>
      <AddressDerivedFields values={values} errors={errors} touched={touched} />

      <Divider sx={{ my: "32px" }} />

      <ReplyEmailField
        value={values.replyToEmail ?? ""}
        onChange={handleChange}
        onBlur={handleBlur}
        error={touched.replyToEmail && !!errors.replyToEmail}
        helperText={touched.replyToEmail && errors.replyToEmail}
      />

      <PromotionalBannerFields
        values={values}
        errors={errors}
        touched={touched}
        handleChange={handleChange}
        handleBlur={handleBlur}
        setFieldValue={setFieldValue}
        setFieldTouched={setFieldTouched}
        uploadError={homeImageError}
        onUploadErrorDismiss={onHomeImageErrorDismiss}
      />
    </Box>
  );
};

const useStyles = makeSxStyles(() => ({
  container: {
    mt: "24px",
    pb: "40px",
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: "24px",
    lineHeight: "32px",
    mb: "32px",
  },
}));

export default AddOrganizationForm;
